/**
 * Surface / Panel — the base container primitive. A warm paper sheet with a
 * configurable elevation (flat / raised / floating / sunken), padding, and
 * corner radius. Card and most overlays build on this language.
 */
import { forwardRef, type HTMLAttributes } from "react";

export type SurfaceElevation = "flat" | "raised" | "floating" | "sunken";
export type SurfacePad = "none" | "sm" | "md" | "lg";
export type SurfaceRadius = "3" | "4" | "5";

export interface SurfaceProps extends HTMLAttributes<HTMLDivElement> {
  /** Drop-shadow depth. @default "raised" */
  readonly elevation?: SurfaceElevation;
  /** Inner padding step. @default "md" */
  readonly pad?: SurfacePad;
  /** Corner radius token. @default "4" */
  readonly radius?: SurfaceRadius;
}

/** Panel is an alias of Surface for semantic clarity at call sites. */
export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { elevation = "raised", pad = "md", radius = "4", className, children, ...rest },
  ref,
) {
  const classes = [
    "lc-surface",
    `lc-surface--${elevation}`,
    `lc-surface--pad-${pad}`,
    `lc-surface--r-${radius}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div ref={ref} className={classes} {...rest}>
      {children}
    </div>
  );
});

export const Panel = Surface;
